import { I18n } from '@calendly/i18n'

import formatErrorMessage from 'core/format_error_message'

/**
 * Maps a formatted error message to its corresponding billing feature key.
 * Compares the formatted message against known policy error messages.
 *
 * @param {string} message - The error message to match
 * @returns {string|null} The feature key if matched, null otherwise
 */
const getFeatureKeyFromMessage = (message) => {
  const formattedMessage = formatErrorMessage(message)

  switch (formattedMessage) {
    case formatErrorMessage(I18n.t('errors.policy.event_type.enforcements.limit', { count: 1 })):
      return 'event_type_limit'
    case formatErrorMessage(I18n.t('errors.policy.event_type.enforcements.group_events')):
      return 'group_event_type'
    case formatErrorMessage(I18n.t('errors.policy.event_type.enforcements.team_events')):
      return 'team_scheduling'
    case formatErrorMessage(I18n.t('errors.policy.event_type.enforcements.collective_event_types')):
      return 'collective_event_types'
    case formatErrorMessage(I18n.t('errors.policy.event_type.enforcements.round_robin_event_types')):
      return 'round_robin_event_types'
    case formatErrorMessage(I18n.t('errors.policy.event_type.enforcements.collective_round_robin_event_types')):
      return 'collective_round_robin_event_types'
    default:
      return null
  }
}

/**
 * Extracts a billing feature key from a policy error object.
 * Supports two error formats:
 * 1. { type: 'policy', payload: { base: [...] } } - New format with payload.base array
 * 2. { type: 'policy', message: '...' } - Legacy format with message string
 *
 * @param {Object} error - The error object to process
 * @param {string} error.type - The error type (must be 'policy' to process)
 * @param {Object} [error.payload] - Optional payload object containing base array
 * @param {string[]} [error.payload.base] - Array of error messages (new format)
 * @param {string} [error.message] - Error message string (legacy format)
 * @returns {string|null} The feature key if matched, null if not a policy error or no match found
 *
 * @example
 * // New format with payload.base
 * getFeatureKeyFromError({
 *   type: 'policy',
 *   payload: { base: ['Your current subscription does not include round robin event types.'] }
 * })
 * // Returns: 'round_robin_event_types'
 *
 * @example
 * // Legacy format with message
 * getFeatureKeyFromError({
 *   type: 'policy',
 *   message: formatErrorMessage(I18n.t('errors.policy.event_type.enforcements.limit', { count: 1 }))
 * })
 * // Returns: 'event_type_limit'
 */
export const getFeatureKeyFromError = (error) => {
  if (error.type !== 'policy') {
    return null
  }

  // Handle format with payload.base: { type: 'policy', payload: { base: [...] } }
  if (error.payload?.base && Array.isArray(error.payload.base) && error.payload.base.length > 0) {
    return getFeatureKeyFromMessage(error.payload.base[0])
  }

  // Handle existing format: { type: 'policy', message: '...' }
  if (error.message) {
    return getFeatureKeyFromMessage(error.message)
  }

  return null
}
